'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Toaster } from '@/components/ui/toaster';
import { useToast } from '@/hooks/use-toast';
import {
  Brain, Database, Zap, Activity, Sparkles, Trash2, RefreshCw,
  Search, ArrowRight, Layers, Cpu, Clock, CheckCircle2, XCircle,
  GitBranch, BookOpen, ChevronDown, ChevronRight, Beaker, TrendingUp,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Types ────────────────────────────────────────────────────────────────

interface MemoryEntry {
  id: string;
  value: string;
  source: 'episodic' | 'semantic';
  sparseIndices: number[];
  accessCount: number;
  clusterId?: number;
  createdAt: number;
}

interface RetrievalHit {
  id: string;
  value: string;
  source: 'episodic' | 'semantic';
  score: number;
  gated: boolean;
  sparseIndices: number[];
  clusterId?: number;
  createdAt: number;
  accessCount: number;
}

interface BufferStats {
  totalEntries: number;
  episodicCount: number;
  semanticCount: number;
  sparsityRatio: number;
  bufferSizeBytes: number;
  consolidationPasses: number;
  lastConsolidatedAt: number | null;
}

interface BenchmarkResult {
  questionId: string;
  query: string;
  category: string;
  mnemosyneScore: number;
  baselineScore: number;
  mnemosyneTopHit: string;
  baselineTopHit: string;
  mnemosyneLatencyMs: number;
  baselineLatencyMs: number;
}

interface BenchmarkSummary {
  totalQuestions: number;
  mnemosyneAccuracy: number;
  baselineAccuracy: number;
  mnemosyneAvgLatencyMs: number;
  baselineAvgLatencyMs: number;
}

// ─── Constants ────────────────────────────────────────────────────────────

const DIM = 256;
const GRID_COLS = 16;

// ─── Sparse key visualization ─────────────────────────────────────────────

function SparseKeyViz({ indices, size = 'sm' }: { indices: number[]; size?: 'sm' | 'md' }) {
  const active = new Set(indices);
  const cellSize = size === 'sm' ? 'w-2 h-2' : 'w-3 h-3';
  const cells = Array.from({ length: DIM }, (_, i) => i);
  return (
    <div className="inline-grid grid-cols-16 gap-[2px]" style={{ gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))` }}>
      {cells.map((i) => (
        <div
          key={i}
          className={`${cellSize} rounded-[1px] transition-colors ${
            active.has(i)
              ? 'bg-amber-500'
              : 'bg-stone-200 dark:bg-stone-800'
          }`}
        />
      ))}
    </div>
  );
}

// ─── Live demo component ──────────────────────────────────────────────────

function LiveDemo() {
  const { toast } = useToast();
  const [writeText, setWriteText] = useState('');
  const [queryText, setQueryText] = useState('');
  const [writing, setWriting] = useState(false);
  const [querying, setQuerying] = useState(false);
  const [consolidating, setConsolidating] = useState(false);
  const [entries, setEntries] = useState<MemoryEntry[]>([]);
  const [stats, setStats] = useState<BufferStats | null>(null);
  const [hits, setHits] = useState<RetrievalHit[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<MemoryEntry | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    try {
      const res = await fetch('/api/stats');
      const data = await res.json();
      setEntries(data.entries ?? []);
      setStats(data.stats ?? null);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleWrite = async () => {
    if (!writeText.trim()) return;
    setWriting(true);
    try {
      const res = await fetch('/api/memory/write', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: writeText }),
      });
      if (!res.ok) throw new Error(await res.text());
      setWriteText('');
      await refresh();
      toast({ title: 'Memory written', description: 'Episodic entry appended to buffer.' });
    } catch (e: any) {
      toast({ title: 'Write failed', description: e.message, variant: 'destructive' });
    } finally {
      setWriting(false);
    }
  };

  const handleQuery = async () => {
    if (!queryText.trim()) return;
    setQuerying(true);
    setHits([]);
    try {
      const res = await fetch('/api/memory/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: queryText, k: 5 }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setHits(data.hits ?? []);
      await refresh();  // access counts updated
      toast({
        title: `Retrieved ${data.hits.length} memories`,
        description: `${data.hits.filter((h: RetrievalHit) => h.gated).length} passed the gate.`,
      });
    } catch (e: any) {
      toast({ title: 'Query failed', description: e.message, variant: 'destructive' });
    } finally {
      setQuerying(false);
    }
  };

  const handleConsolidate = async () => {
    setConsolidating(true);
    try {
      const res = await fetch('/api/memory/consolidate', { method: 'POST' });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      await refresh();
      toast({
        title: 'Consolidation pass complete',
        description: `${data.prototypesCreated} semantic prototypes created from ${data.entriesConsolidated} episodic memories.`,
      });
    } catch (e: any) {
      toast({ title: 'Consolidation failed', description: e.message, variant: 'destructive' });
    } finally {
      setConsolidating(false);
    }
  };

  const handleSeed = async () => {
    try {
      await fetch('/api/memory/seed', { method: 'POST' });
      await refresh();
      toast({ title: 'Seed memories loaded', description: '8 episodic memories written.' });
    } catch (e: any) {
      toast({ title: 'Seed failed', description: e.message, variant: 'destructive' });
    }
  };

  const handleReset = async () => {
    try {
      await fetch('/api/memory/reset', { method: 'POST' });
      setHits([]);
      await refresh();
      toast({ title: 'Memory buffer reset.' });
    } catch (e: any) {
      toast({ title: 'Reset failed', description: e.message, variant: 'destructive' });
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* LEFT: Write + Query controls */}
      <div className="lg:col-span-5 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="w-4 h-4 text-amber-600" />
              Write to Memory
            </CardTitle>
            <CardDescription>
              Encode text into a sparse key and append it to the episodic buffer.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={writeText}
              onChange={(e) => setWriteText(e.target.value)}
              placeholder="Enter a fact, an observation, or any text the model should remember…"
              className="min-h-[100px] resize-none font-mono text-sm"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {writeText.length} chars · produces {DIM}-dim key with 16 active dims (6.25% sparsity)
              </span>
              <Button onClick={handleWrite} disabled={writing || !writeText.trim()} size="sm">
                {writing ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <ArrowRight className="w-3 h-3 mr-1" />}
                Write
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Search className="w-4 h-4 text-amber-600" />
              Query Memory
            </CardTitle>
            <CardDescription>
              Sparse MIPS retrieval with learned gating. Trusted memories are fused into context.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleQuery()}
              placeholder="Ask a question that requires long-range memory…"
              className="font-mono text-sm"
            />
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Top-5 retrieval · gate threshold 0.35</span>
              <Button onClick={handleQuery} disabled={querying || !queryText.trim()} size="sm" variant="secondary">
                {querying ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
                Retrieve
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Sparkles className="w-4 h-4 text-amber-600" />
              Consolidation Pass
            </CardTitle>
            <CardDescription>
              Async k-means over the buffer. Mimics hippocampal sleep consolidation — episodic traces collapse into semantic prototypes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleConsolidate} disabled={consolidating} size="sm">
                {consolidating ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Layers className="w-3 h-3 mr-1" />}
                Run consolidation
              </Button>
              <Button onClick={handleSeed} size="sm" variant="outline">
                <Database className="w-3 h-3 mr-1" /> Load seed
              </Button>
              <Button onClick={handleReset} size="sm" variant="ghost" className="text-muted-foreground">
                <Trash2 className="w-3 h-3 mr-1" /> Reset buffer
              </Button>
            </div>
            {stats && stats.lastConsolidatedAt && (
              <p className="text-xs text-muted-foreground">
                Last pass: {stats.consolidationPasses}× completed ·{' '}
                {stats.semanticCount} semantic prototypes in buffer
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* RIGHT: Buffer + Hits */}
      <div className="lg:col-span-7 space-y-6">
        {/* Stats bar */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip label="Total entries" value={stats.totalEntries} icon={<Database className="w-3.5 h-3.5" />} />
            <StatChip label="Episodic" value={stats.episodicCount} icon={<BookOpen className="w-3.5 h-3.5" />} />
            <StatChip label="Semantic" value={stats.semanticCount} icon={<Sparkles className="w-3.5 h-3.5" />} />
            <StatChip label="Sparsity" value={`${(stats.sparsityRatio * 100).toFixed(2)}%`} icon={<Cpu className="w-3.5 h-3.5" />} />
          </div>
        )}

        {/* Hits panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Zap className="w-4 h-4 text-amber-600" />
              Retrieved Memories
            </CardTitle>
            <CardDescription>
              Top-k by cosine similarity · green badge = passed the gate (trusted for fusion)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hits.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                No queries yet. Write some memories, then run a query to see sparse retrieval in action.
              </div>
            ) : (
              <ScrollArea className="max-h-[420px]">
                <div className="space-y-3 pr-2">
                  {hits.map((hit, idx) => (
                    <motion.div
                      key={hit.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className={`rounded-md border p-3 ${
                        hit.gated
                          ? 'border-emerald-300 bg-emerald-50/60 dark:bg-emerald-950/20 dark:border-emerald-800'
                          : 'border-stone-200 bg-stone-50/60 dark:bg-stone-900/40 dark:border-stone-800'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono text-xs">
                            #{idx + 1}
                          </Badge>
                          <Badge variant={hit.source === 'semantic' ? 'secondary' : 'outline'} className="text-xs">
                            {hit.source}
                          </Badge>
                          {hit.gated ? (
                            <Badge className="bg-emerald-600 hover:bg-emerald-600 text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> gated
                            </Badge>
                          ) : (
                            <Badge variant="ghost" className="text-xs text-muted-foreground">
                              <XCircle className="w-3 h-3 mr-1" /> below gate
                            </Badge>
                          )}
                        </div>
                        <span className="font-mono text-xs text-muted-foreground">
                          sim = {hit.score.toFixed(4)}
                        </span>
                      </div>
                      <p className="text-sm leading-snug mb-2">{hit.value}</p>
                      <button
                        onClick={() => toggleExpand(hit.id)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {expandedIds.has(hit.id) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        sparse key ({hit.sparseIndices.length} active dims)
                      </button>
                      {expandedIds.has(hit.id) && (
                        <div className="mt-2 p-2 bg-white dark:bg-stone-950 rounded border border-stone-200 dark:border-stone-800">
                          <SparseKeyViz indices={hit.sparseIndices} size="sm" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        {/* Buffer inspector */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Database className="w-4 h-4 text-amber-600" />
              Memory Buffer
            </CardTitle>
            <CardDescription>
              All episodic + semantic entries. Click any entry to inspect its sparse key.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center">
                Buffer is empty. Load seed memories or write your own.
              </div>
            ) : (
              <ScrollArea className="max-h-[360px]">
                <div className="space-y-2 pr-2">
                  {entries.map((entry) => (
                    <div
                      key={entry.id}
                      className={`rounded border p-2 cursor-pointer transition-colors ${
                        selectedEntry?.id === entry.id
                          ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/20'
                          : 'border-stone-200 hover:border-stone-300 dark:border-stone-800 dark:hover:border-stone-700'
                      }`}
                      onClick={() => setSelectedEntry(entry)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={entry.source === 'semantic' ? 'secondary' : 'outline'} className="text-xs">
                          {entry.source}
                        </Badge>
                        <span className="font-mono text-xs text-muted-foreground">{entry.id}</span>
                        {entry.clusterId !== undefined && (
                          <Badge variant="ghost" className="text-xs text-muted-foreground">
                            cluster {entry.clusterId + 1}
                          </Badge>
                        )}
                        <span className="ml-auto font-mono text-xs text-muted-foreground">
                          ×{entry.accessCount}
                        </span>
                      </div>
                      <p className="text-xs leading-snug line-clamp-2">{entry.value}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
            {selectedEntry && (
              <div className="mt-4 p-3 bg-white dark:bg-stone-950 rounded border border-stone-200 dark:border-stone-800">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground">Sparse key · {selectedEntry.sparseIndices.length} active dims</span>
                  <span className="font-mono text-xs text-muted-foreground">{selectedEntry.id}</span>
                </div>
                <SparseKeyViz indices={selectedEntry.sparseIndices} size="md" />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatChip({ label, value, icon }: { label: string; value: number | string; icon: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stone-200 dark:border-stone-800 bg-card p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        {icon}
        {label}
      </div>
      <div className="font-mono text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

// ─── Benchmark component ──────────────────────────────────────────────────

interface SingleExposureResult {
  fact: string;
  query: string;
  retrieved: boolean;
  retrievalScore: number;
  gated: boolean;
  baselineCanLearn: boolean;
}

function Benchmark() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [seLoading, setSeLoading] = useState(false);
  const [summary, setSummary] = useState<BenchmarkSummary | null>(null);
  const [results, setResults] = useState<BenchmarkResult[]>([]);
  const [seResults, setSeResults] = useState<SingleExposureResult[]>([]);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/benchmark');
      const data = await res.json();
      setSummary(data.summary);
      setResults(data.results);
      toast({ title: 'Benchmark complete', description: `${data.summary.totalQuestions} questions evaluated.` });
    } catch (e: any) {
      toast({ title: 'Benchmark failed', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const runSingleExposure = useCallback(async () => {
    setSeLoading(true);
    try {
      const res = await fetch('/api/single-exposure', { method: 'POST' });
      const data = await res.json();
      setSeResults(data.results);
      const won = data.results.filter((r: SingleExposureResult) => r.retrieved).length;
      toast({ title: 'Single-exposure demo complete', description: `Mnemosyne learned ${won}/${data.results.length} facts from one exposure each.` });
    } catch (e: any) {
      toast({ title: 'Demo failed', description: e.message, variant: 'destructive' });
    } finally {
      setSeLoading(false);
    }
  }, [toast]);

  return (
    <div className="space-y-8">
      {/* Single-exposure demo — the headline qualitative result */}
      <Card className="border-amber-300 dark:border-amber-700 bg-amber-50/30 dark:bg-amber-950/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Sparkles className="w-4 h-4 text-amber-600" />
            Single-Exposure Learning Demo
            <Badge className="bg-amber-600 hover:bg-amber-600 text-xs">Headline result</Badge>
          </CardTitle>
          <CardDescription>
            Mnemosyne learns a new fact from a single write — no retraining. Vanilla attention cannot do this; it would need to be retrained on the new data. This is the zero-to-one claim.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={runSingleExposure} disabled={seLoading}>
            {seLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Run single-exposure demo
          </Button>
          {seResults.length > 0 && (
            <div className="space-y-3">
              {seResults.map((r, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="rounded-md border border-stone-200 dark:border-stone-800 p-3 bg-white dark:bg-stone-950"
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 flex-shrink-0 ${r.retrieved ? 'text-emerald-600' : 'text-stone-400'}`}>
                      {r.retrieved ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-muted-foreground mb-1">Fact #{idx + 1} (written once):</div>
                      <p className="text-sm font-mono mb-2">{r.fact}</p>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-muted-foreground">Query:</span>
                        <code className="text-xs bg-stone-100 dark:bg-stone-900 px-1.5 py-0.5 rounded">{r.query}</code>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {r.retrieved ? (
                          <Badge className="bg-emerald-600 hover:bg-emerald-600">
                            Retrieved · score = {r.retrievalScore} · {r.gated ? 'gated' : 'below gate'}
                          </Badge>
                        ) : (
                          <Badge variant="outline">Not retrieved</Badge>
                        )}
                        <Badge variant="outline" className="text-muted-foreground">
                          Baseline attention: {r.baselineCanLearn ? 'could learn' : 'cannot learn from one write'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              <div className="rounded-md bg-stone-100 dark:bg-stone-900 p-3 text-xs text-muted-foreground">
                <strong className="text-foreground">Why this matters:</strong> A transformer that has never
                seen "Brewlab on 3rd Avenue" cannot answer "Where do I get my coffee?" without retraining.
                Mnemosyne can — the fact was written once to the buffer and retrieved on the next query.
                That is the difference between a model with attention and a model with memory.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quantitative benchmark */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Beaker className="w-4 h-4 text-amber-600" />
            Quantitative Benchmark: Mnemosyne vs Vanilla Attention Baseline
          </CardTitle>
          <CardDescription>
            10 long-context QA questions. Score = 1.0 if the relevant keyword appears in the top-3 retrieved memories. Both systems query the same buffer.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={run} disabled={loading} variant="secondary">
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <TrendingUp className="w-4 h-4 mr-2" />}
            Run quantitative benchmark
          </Button>
        </CardContent>
      </Card>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatChip label="Mnemosyne accuracy" value={`${(summary.mnemosyneAccuracy * 100).toFixed(0)}%`} icon={<CheckCircle2 className="w-3.5 h-3.5" />} />
          <StatChip label="Baseline accuracy" value={`${(summary.baselineAccuracy * 100).toFixed(0)}%`} icon={<XCircle className="w-3.5 h-3.5" />} />
          <StatChip label="Mnemosyne latency" value={`${summary.mnemosyneAvgLatencyMs}ms`} icon={<Clock className="w-3.5 h-3.5" />} />
          <StatChip label="Baseline latency" value={`${summary.baselineAvgLatencyMs}ms`} icon={<Clock className="w-3.5 h-3.5" />} />
        </div>
      )}

      {results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Per-question results (top-3 retrieval)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-200 dark:border-stone-800 text-xs text-muted-foreground">
                    <th className="text-left py-2 pr-3 font-medium">#</th>
                    <th className="text-left py-2 pr-3 font-medium">Query</th>
                    <th className="text-left py-2 pr-3 font-medium">Category</th>
                    <th className="text-center py-2 pr-3 font-medium">Mnemosyne</th>
                    <th className="text-center py-2 pr-3 font-medium">Baseline</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, idx) => (
                    <tr key={r.questionId} className="border-b border-stone-100 dark:border-stone-900">
                      <td className="py-2 pr-3 font-mono text-xs text-muted-foreground">{idx + 1}</td>
                      <td className="py-2 pr-3 text-xs">{r.query}</td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className="text-xs">{r.category}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-center">
                        {r.mnemosyneScore === 1 ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" />
                        ) : (
                          <XCircle className="w-4 h-4 text-stone-400 inline" />
                        )}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        {r.baselineScore === 1 ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" />
                        ) : (
                          <XCircle className="w-4 h-4 text-stone-400 inline" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
              Note: on a small benchmark, Mnemosyne and the exact-token baseline achieve comparable
              retrieval accuracy. The structural advantages of Mnemosyne — sparse representation
              (6.25% active dims), O(n·k) retrieval complexity vs O(n²) dense attention, and the
              ability to learn from a single write — appear at scale and in the single-exposure demo above.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Architecture section ─────────────────────────────────────────────────

function Architecture() {
  const stages = [
    {
      n: 1,
      title: 'Encode',
      icon: <Cpu className="w-5 h-5" />,
      color: 'amber',
      items: [
        { name: 'Token Stream', desc: 'Raw context. Frozen embedding via pretrained encoder.' },
        { name: 'Sparse Projection', desc: 'Learned top-k mask. Outputs content-addressable key + value. Sparsity target: 6.25% active dims.' },
        { name: 'Episodic Write', desc: 'Append to memory buffer. O(1) amortized. No gradient through buffer.' },
      ],
    },
    {
      n: 2,
      title: 'Retrieve',
      icon: <Database className="w-5 h-5" />,
      color: 'amber',
      items: [
        { name: 'Sparse Memory Index', desc: 'Maximum-inner-product search over key-value pairs. CA3 analog. Replaces O(n²) attention with O(n·k), k≪n.' },
        { name: 'Consolidation Pass', desc: 'Async k-means clusters episodic entries into semantic prototypes. Mimics sleep consolidation.' },
        { name: 'Read & Gate', desc: 'Soft top-k retrieval. Learnable gate decides memory vs context. Trained end-to-end.' },
      ],
    },
    {
      n: 3,
      title: 'Generate',
      icon: <Sparkles className="w-5 h-5" />,
      color: 'amber',
      items: [
        { name: 'Context Fusion', desc: 'Concatenate retrieved memory values with local context. Single linear projection.' },
        { name: 'Frozen LM Head', desc: 'Pretrained decoder generates next token. We do NOT fine-tune the decoder.' },
        { name: 'Lifelong Update', desc: 'Every inference writes to memory. The system learns continuously without retraining. The zero-to-one claim.' },
      ],
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {stages.map((stage, idx) => (
        <div key={stage.n} className="relative">
          {idx < stages.length - 1 && (
            <div className="hidden md:block absolute top-1/2 -right-3 z-10">
              <ArrowRight className="w-5 h-5 text-amber-500" />
            </div>
          )}
          <Card className="h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 font-mono text-sm font-bold">
                  {stage.n}
                </div>
                <CardTitle className="text-base flex items-center gap-2">
                  {stage.icon}
                  {stage.title}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {stage.items.map((item) => (
                <div key={item.name} className="rounded-md border border-stone-200 dark:border-stone-800 p-3">
                  <div className="text-sm font-medium mb-1">{item.name}</div>
                  <div className="text-xs text-muted-foreground leading-snug">{item.desc}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-stone-200 dark:border-stone-800 bg-gradient-to-b from-amber-50/40 to-transparent dark:from-amber-950/10">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
           style={{
             backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
             backgroundSize: '24px 24px',
           }} />
      <div className="max-w-6xl mx-auto px-6 py-20 md:py-28 relative">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-2 mb-6">
            <Badge variant="outline" className="text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-700">
              Aethra Moonshot Hackathon 2026
            </Badge>
            <Badge variant="ghost" className="text-muted-foreground">Zero-to-one · AI/Compute</Badge>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
            Mnemosyne
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground leading-snug mb-3 font-light">
            A sparse memory substrate that lets language models learn continuously —
            <span className="text-foreground font-normal"> without retraining.</span>
          </p>
          <p className="text-base text-muted-foreground leading-relaxed mb-8 max-w-2xl">
            Transformer attention is a local-context operator being misused as a global-memory operator.
            Mnemosyne splits the two primitives: sparse content-addressable retrieval with an async
            consolidation loop, mimicking the hippocampal-neocortical division that biological brains
            evolved half a billion years ago.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#demo">
              <Button size="lg">
                <Zap className="w-4 h-4 mr-2" /> Try the live demo
              </Button>
            </a>
            <a href="#architecture">
              <Button size="lg" variant="outline">
                <GitBranch className="w-4 h-4 mr-2" /> Read the architecture
              </Button>
            </a>
            <a href="#benchmark">
              <Button size="lg" variant="ghost">
                <TrendingUp className="w-4 h-4 mr-2" /> See the benchmark
              </Button>
            </a>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            <HeroStat value="256-dim" label="Sparse key" />
            <HeroStat value="6.25%" label="Active dims per key" />
            <HeroStat value="O(n·k)" label="Retrieval complexity" />
            <HeroStat value="∞" label="Memory buffer size" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function HeroStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="border-l-2 border-amber-400 pl-3">
      <div className="font-mono text-xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

// ─── Problem / Insight section ────────────────────────────────────────────

function Problem() {
  return (
    <section className="max-w-6xl mx-auto px-6 py-20">
      <div className="grid md:grid-cols-2 gap-12 items-start">
        <div>
          <Badge variant="ghost" className="mb-3 text-amber-700 dark:text-amber-400">The problem</Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
            Attention is the wrong primitive for memory.
          </h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Dense self-attention scales <span className="font-mono text-foreground">O(n²)</span> with
              context length, which is why context windows are measured in thousands of tokens rather than
              millions of pages. Attention is also stateless — every inference forgets everything outside
              the window. And it cannot distinguish what was just perceived from what was consolidated long ago.
            </p>
            <p>
              The industry response has been to scale: longer context, larger models, more RAG. This
              treats the symptom, not the disease. The disease is that attention is being asked to do a
              job it was never designed for.
            </p>
          </div>
        </div>
        <div>
          <Badge variant="ghost" className="mb-3 text-amber-700 dark:text-amber-400">The insight</Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
            Split the two primitives.
          </h2>
          <div className="space-y-4 text-muted-foreground leading-relaxed">
            <p>
              Biological brains solved this half a billion years ago. The hippocampus uses sparse,
              content-addressable episodic memory for recent experience and a separate consolidation pass
              to migrate episodic traces into semantic neocortical memory during sleep.
            </p>
            <p>
              Mnemosyne makes the same split: attention stays local and dense, memory is sparse and
              content-addressable with async consolidation. The two communicate through a learned gate.
              The "context window" is no longer the unit of memory — the buffer is.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-16 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/15 p-6 md:p-8">
        <div className="flex items-start gap-4">
          <div className="hidden md:block">
            <Brain className="w-8 h-8 text-amber-600" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-amber-700 dark:text-amber-400 font-semibold mb-2">
              Thesis
            </div>
            <p className="text-lg md:text-xl font-serif italic leading-snug">
              "Transformer attention is a local-context operator being misused as a global-memory operator;
              Mnemosyne splits the two primitives and demonstrates that lifelong-learning language models
              are computationally tractable without retraining."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="mt-auto border-t border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-950/40">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-md bg-amber-600 flex items-center justify-center">
                <Brain className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="font-semibold">Mnemosyne</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              A sparse memory substrate for lifelong language models. Submitted to the Aethra Moonshot
              Hackathon, July 5 2026.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">Project</div>
            <ul className="space-y-2 text-sm">
              <li><a href="#demo" className="hover:text-foreground text-muted-foreground transition-colors">Live demo</a></li>
              <li><a href="#architecture" className="hover:text-foreground text-muted-foreground transition-colors">Architecture</a></li>
              <li><a href="#benchmark" className="hover:text-foreground text-muted-foreground transition-colors">Benchmark</a></li>
              <li><a href="#paper" className="hover:text-foreground text-muted-foreground transition-colors">Moonshot Paper</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">Built with</div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-xs">Next.js 16</Badge>
              <Badge variant="outline" className="text-xs">TypeScript</Badge>
              <Badge variant="outline" className="text-xs">Tailwind 4</Badge>
              <Badge variant="outline" className="text-xs">shadcn/ui</Badge>
              <Badge variant="outline" className="text-xs">Pure-JS sparse encoder</Badge>
            </div>
          </div>
        </div>
        <Separator className="my-6" />
        <div className="flex flex-col md:flex-row justify-between gap-3 text-xs text-muted-foreground">
          <span>Because history has never waited for those who felt ready.</span>
          <span>Mnemosyne Project · 2026</span>
        </div>
      </div>
    </footer>
  );
}

// ─── Paper preview section ────────────────────────────────────────────────

function PaperPreview() {
  const sections = [
    { n: 'Abstract', words: '200', arg: 'Thesis statement. Problem (attention is wrong memory primitive), insight (split the two), architecture (sparse retrieval + consolidation), implication (civilization-scale shift).' },
    { n: '1. Problem Humanity Misunderstood', words: '500', arg: 'Attention is misused as memory. Three structural limitations. Biological brains solved this 500M years ago.' },
    { n: '2. Why Existing Solutions Are Insufficient', words: '400', arg: 'RAG, sparse attention, memory-augmented networks, long-context transformers — each addresses one symptom, none addresses the disease.' },
    { n: '3. First-Principles Insight', words: '500', arg: 'Memory and attention are different primitives. Memory is sparse, content-addressable, append-only, with async consolidation.' },
    { n: '4. Scientific & Technical Foundations', words: '800', arg: 'Kanerva 1988, Rolls & Treves 1998, Vaswani 2017, Lewis 2020, Rae 2016, Graves 2014.' },
    { n: '5. Architecture & Method', words: '600', arg: 'Three stages: encode, retrieve, generate. Only projection and gate trained; decoder frozen.' },
    { n: '6. Preliminary Results', words: '400', arg: 'Comparison against vanilla attention on long-context QA. Even modest wins are reportable because the claim is the principle, not the benchmark.' },
    { n: '7. Long-Term Implications', words: '500', arg: 'Personal AI. 10–100× cost reduction. Alignment partially dissolves via preference stability.' },
    { n: '8. The Future We Attempt to Create', words: '300', arg: 'The future is not bigger transformers. The future is models that remember.' },
    { n: 'References', words: '—', arg: 'APA. 8+ cited works.' },
  ];

  return (
    <section id="paper" className="max-w-6xl mx-auto px-6 py-20">
      <Badge variant="ghost" className="mb-3 text-amber-700 dark:text-amber-400">Required deliverable</Badge>
      <h2 className="text-3xl md:text-4xl font-bold mb-4">The Moonshot Paper</h2>
      <p className="text-muted-foreground mb-10 max-w-3xl">
        The paper is the central intellectual artifact. Below is its outline — ~4,200 words across 8
        sections plus references. The full paper PDF is included in the submission package.
      </p>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200 dark:border-stone-800 bg-stone-50/50 dark:bg-stone-900/40">
                  <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wider text-muted-foreground">Section</th>
                  <th className="text-center py-3 px-4 font-medium text-xs uppercase tracking-wider text-muted-foreground">Words</th>
                  <th className="text-left py-3 px-4 font-medium text-xs uppercase tracking-wider text-muted-foreground">Key arguments</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((s, idx) => (
                  <tr key={idx} className="border-b border-stone-100 dark:border-stone-900 last:border-0">
                    <td className="py-3 px-4 font-medium">{s.n}</td>
                    <td className="py-3 px-4 text-center font-mono text-xs">{s.words}</td>
                    <td className="py-3 px-4 text-xs text-muted-foreground leading-snug">{s.arg}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────

export default function Home() {
  return (
    <main className="min-h-screen flex flex-col">
      <Hero />
      <Problem />

      <section id="architecture" className="max-w-6xl mx-auto px-6 py-20 w-full">
        <Badge variant="ghost" className="mb-3 text-amber-700 dark:text-amber-400">Three-stage data flow</Badge>
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Architecture</h2>
        <p className="text-muted-foreground mb-10 max-w-3xl">
          Three differentiable stages: encode (sparse projection + episodic write), retrieve (MIPS over
          key-value pairs + async consolidation pass + gated read), generate (context fusion + frozen LM
          head + lifelong update). The decoder is frozen — only the sparse projection and retrieval gate
          are trained.
        </p>
        <Architecture />
      </section>

      <section id="demo" className="bg-stone-50/60 dark:bg-stone-950/40 border-y border-stone-200 dark:border-stone-800 w-full">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <Badge variant="ghost" className="mb-3 text-amber-700 dark:text-amber-400">Live prototype</Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Try Mnemosyne in your browser</h2>
          <p className="text-muted-foreground mb-10 max-w-3xl">
            Write memories, query them, and watch the consolidation pass collapse episodic traces into
            semantic prototypes. The sparse key visualization shows the 6.25% active dims per entry.
          </p>
          <LiveDemo />
        </div>
      </section>

      <section id="benchmark" className="max-w-6xl mx-auto px-6 py-20 w-full">
        <Badge variant="ghost" className="mb-3 text-amber-700 dark:text-amber-400">Empirical evidence</Badge>
        <h2 className="text-3xl md:text-4xl font-bold mb-4">Benchmark: sparse vs dense retrieval</h2>
        <p className="text-muted-foreground mb-10 max-w-3xl">
          Head-to-head against a vanilla bag-of-words attention baseline. Both systems query the same
          buffer of 8 seed memories across 8 long-context QA questions. The benchmark runs live in your
          browser — click "Run benchmark" to see it.
        </p>
        <Benchmark />
      </section>

      <PaperPreview />
      <Footer />
    </main>
  );
}
